import { SimpleQuestionBlock, QnA_Block, QuestionType, type RankQuestionBlock } from "@/q-n-a.ts";
import { Box, Group, Text, Stack, Button, Paper, SegmentedControl, Image } from '@mantine/core';
import { useState, useMemo, useEffect } from 'react';
import { useListState } from "@mantine/hooks";
import { groupQuestionsWithImages } from "@/question.grouping";
import RankQuestion from "../componets/rank.question.tsx";

export default function SingleSidedPrompts(props: {
    questions: QnA_Block[],
    onSubmit: (answers: QnA_Block[]) => void,
    onProgress: (qid: string | number) => void
}) {
    props.questions.forEach(q => {
        if( !q.answerLog) q.answerLog = {};
    });
    
    // Separate questions by type
    const simpleQuestions = props.questions.filter((q): q is SimpleQuestionBlock => 
        'qText' in q && q.qType !== QuestionType.RANK
    );
    const rankQuestions = props.questions.filter((q): q is RankQuestionBlock => 
        q.qType === QuestionType.RANK || 'question' in q
    );
    
    const [simpleAnswers, simpleHandlers] = useListState<SimpleQuestionBlock>(simpleQuestions);
    const [rankAnswers, rankHandlers] = useListState<RankQuestionBlock>(rankQuestions);
    
    // Combine all answers for grouping and submission
    const allAnswers = useMemo(() => {
        return [...simpleAnswers, ...rankAnswers] as QnA_Block[];
    }, [simpleAnswers, rankAnswers]);
    
    const [currentGroup, setCurrentGroup] = useState(0);
    const [manuallyNavigated, setManuallyNavigated] = useState(false);

    const QUESTIONS_PER_GROUP = 5;
    
    // Use the new grouping logic
    const questionGroups = useMemo(() => {
        const grouped = groupQuestionsWithImages(allAnswers, QUESTIONS_PER_GROUP);
        return grouped;
    }, [allAnswers]);
    
    const totalGroups = questionGroups.length;

    const getCurrentQuestions = () => {
        return questionGroups[currentGroup] || [];
    };

    const handleContinue = () => {
        if (currentGroup < totalGroups - 1) {
            setCurrentGroup(prev => prev + 1);
            setManuallyNavigated(false); // Reset manual navigation flag
        } else {
            props.onSubmit(allAnswers);
        }
    };

    const handleBack = () => {
        if (currentGroup > 0) {
            setCurrentGroup(prev => prev - 1);
            setManuallyNavigated(true); // Mark as manually navigated
        }
    };

    const isCurrentGroupComplete = () => {
        const currentQuestions = getCurrentQuestions();
        return currentQuestions.every((q: QnA_Block) => {
            if (q.qType === QuestionType.RANK || 'question' in q) {
                return (q as RankQuestionBlock).answer !== undefined;
            }
            return (q as SimpleQuestionBlock).answer !== undefined;
        });
    };
    
    // Handle rank question updates
    const handleRankQuestionChange = (updatedQuestion: RankQuestionBlock) => {
        rankHandlers.applyWhere(
            q => q.qid === updatedQuestion.qid,
            () => updatedQuestion
        );
    };

    // Auto-advance when current group becomes complete (only if not manually navigated)
    useEffect(() => {
        if (isCurrentGroupComplete() && currentGroup < totalGroups - 1 && !manuallyNavigated) {
            const timer = setTimeout(() => {
                handleContinue();
            }, 500); // Small delay to allow user to see the completion
            return () => clearTimeout(timer);
        }
    }, [allAnswers, currentGroup, totalGroups, manuallyNavigated])

    const currentQuestions = getCurrentQuestions();

    if (currentQuestions.length === 0) {
        return (
            <Box>
                <Text>No questions available for this group.</Text>
                <Button
                    onClick={handleContinue}
                    disabled={!isCurrentGroupComplete()}
                >
                    {currentGroup < totalGroups - 1 ? 'Continue' : 'Next'}
                </Button>
            </Box>
        );
    }

    return (
        <Box>
            <Stack gap="md">
                {currentQuestions.map((question: QnA_Block) => {
                    // Check if it's a ranking question
                    if (question.qType === QuestionType.RANK || 'question' in question) {
                        const rankQuestion = question as RankQuestionBlock;
                        return (
                            <RankQuestion
                                key={question.qid}
                                question={rankQuestion}
                                onChange={handleRankQuestionChange}
                                onProgress={props.onProgress}
                            />
                        );
                    }
                    
                    // Otherwise, it's a simple question
                    const simpleQuestion = question as SimpleQuestionBlock;
                    return (
                        
                        <Paper
                            key={simpleQuestion.qid}
                            p="sm"
                        >
                            <Stack gap={4} justify="center">
                                <Text size="1.1rem">
                                    {simpleQuestion.qText || `[Missing qText for qid: ${simpleQuestion.qid}]`}
                                </Text>
                                <SegmentedControl
                                    fullWidth
                                    value={simpleQuestion.answer?.toString()}
                                    onChange={(value) => simpleHandlers.applyWhere(
                                        q => q.qid === simpleQuestion.qid,
                                        q => {
                                            const al = q.answerLog || {};
                                            al[Date.now()] = parseInt(value);
                                            props.onProgress(q.qid);
                                            return({ ...q, answerLog: al, answer: parseInt(value) as 0 | 2 | 1 | -2 | -1 })
                                        }
                                    )}
                                    data={[-99, -2, -1, 0, 1, 2].map(value => {
                                        switch (value) {
                                            case -2:
                                                return { label:'🙅', value: value.toString() };
                                            case -1:
                                                return { label: 'Slightly Disagree', value: value.toString() };
                                            case 1:
                                                return { label: 'Slightly Agree', value: value.toString() };
                                            case 2:
                                                return { label: '🙆', value: value.toString() };
                                            case 0:
                                                return { label: '😶', value: value.toString() };
                                            default:
                                                return { label: 'Pick', value: 'NaN', disabled: !!simpleQuestion.answer };
                                        }
                                    })}
                                    color={(()=> {
                                        switch (simpleQuestion.answer) {
                                            case -2:
                                                return 'var(--mantine-color-yenta-0)';
                                            case -1:
                                                return 'var(--mantine-color-yenta-4)';
                                            case 1:
                                                return 'var(--mantine-color-yenta-6)';
                                            case 2:
                                                return 'var(--mantine-color-yenta-9)';
                                            case 0:
                                            default:
                                                return 'gray';
                                        }
                                    })()}
                                    radius={24}
                                />
                                {/* {simpleQuestion.qUrl && (
                                    <Image 
                                        src={simpleQuestion.qUrl} 
                                        alt={simpleQuestion.qText || 'Question image'} 
                                        h={260} 
                                        w='auto' 
                                        fit='contain' 
                                        mx='auto'
                                        onError={(e) => {
                                            console.error('SingleSidedPrompts: Image failed to load:', simpleQuestion.qUrl, e);
                                        }}
                                        onLoad={() => {
                                            console.log('SingleSidedPrompts: Image loaded successfully:', simpleQuestion.qUrl);
                                        }}
                                    />
                                )} */}
                                { !!simpleQuestion.qUrl && <picture>
                                    <source srcSet={`${simpleQuestion.qUrl}.avif`} type='image/avif' />
                                    <source srcSet={`${simpleQuestion.qUrl}.webp`} type='image/webp' />
                                    <img src={`${simpleQuestion.qUrl}.gif`} alt='Fun Image' loading="lazy" height={260} width={'auto'} style={{margin: 'auto', objectFit: 'contain'}} />
                                </picture>}
                            </Stack>
                        </Paper>
                    );
                })}
            </Stack>

            <Group justify="center" mt="xl" gap="md">
                {currentGroup > 0 && (
                    <Button
                        onClick={handleBack}
                        variant="light"
                    >
                        Back
                    </Button>
                )}
                <Button
                    onClick={handleContinue}
                    disabled={!isCurrentGroupComplete()}
                >
                    {currentGroup < totalGroups - 1 ? 'Continue' : 'Next'}
                </Button>
            </Group>
        </Box>
    );
} 